import { useRef } from 'react';
import { Link } from 'react-router';
import {
    useTracker,
    useImpression,
    ARCHIPRO_IMPRESSION_TRACKER,
} from '@archipro-website/tracker';
import type { FeaturedVideo } from '~/modules-rocco/featured-item/data-mappers/featured-item-to-video';
import { cn } from '@rocco/utils/cn';

interface FeaturedBannerProps {
    /** Banner data to display, null if no banner should be shown */
    banner: FeaturedVideo | null;
    /** Where the banner is displayed — used for click and impression tracking */
    section: 'Grid' | 'LandingPage';
}

/**
 * Featured Banner component for the Products category page.
 * Displays a full-width clickable banner with background image,
 * "FEATURED" label, author name, and title.
 *
 * Design specifications (Figma node 20625:2550):
 * - Full viewport width, height 630px desktop / ~315px mobile
 * - "FEATURED" pill in top-left corner (grey background)
 * - Centered author/subtitle name (uppercase, ~22px, letter-spacing 1.7px)
 * - Centered title (TWKGhost font, ~67px, negative letter-spacing)
 * - Dark overlay for text readability
 * - Entire banner is clickable
 */
export const FeaturedBanner = ({ banner, section }: FeaturedBannerProps) => {
    const tracker = useTracker();
    const sectionRef = useRef<HTMLElement | null>(null);

    // Impression tracking: fires when banner is 50% visible for 1 second (MRC standard)
    useImpression({
        ref: sectionRef,
        disable: !banner || !banner.video.videoCover,
        onImpression: async () => {
            if (!banner) return;
            await tracker.log('Impression', {
                url: new URL(window.location.href),
                data: {
                    ID: banner.id,
                    type: 'Product_Featured_Banner',
                    ProfessionalID: banner.professionalId ?? 0,
                    Section: section,
                },
                targetTracker: ARCHIPRO_IMPRESSION_TRACKER,
            });
        },
    });

    // Don't render if no banner data or no cover image
    if (!banner || !banner.video.videoCover) {
        return null;
    }

    // Use subtitle as override for professional name if provided
    const displayName = banner.subtitle || banner.professionalName;

    /**
     * Handles click tracking for analytics
     */
    const handleBannerClick = () => {
        tracker.log('ProductFeaturedBanner', {
            data: {
                ProfessionalID: banner.professionalId ?? 0,
                ProfessionalTitle: banner.professionalName ?? '',
                ExternalURL: banner.ctaLink,
                Section: section,
                ContentType: banner.contentType,
            },
            label: banner.itemTitle || 'Featured Banner',
            url: new URL(window.location.href),
        });
    };

    const isExternalLink =
        banner.ctaLink.startsWith('http') &&
        !banner.ctaLink.includes('archipro.co');

    // Common content for the banner
    const bannerContent = (
        <>
            {/* Background Image */}
            <img
                src={banner.video.videoCover}
                alt={banner.itemTitle || 'Featured content'}
                className="absolute inset-0 size-full object-cover"
                loading="lazy"
                decoding="async"
            />

            {/* Dark Overlay for text readability */}
            <div className="absolute inset-0 bg-black/30" aria-hidden="true" />

            {/* Content Container */}
            <div className="absolute inset-0 z-10 flex flex-col">
                {/* Top-left: FEATURED pill */}
                <div className="px-4 pt-4 md:px-5 md:pt-6">
                    <span
                        className={cn(
                            'inline-flex items-center justify-center rounded-full bg-[#f2f2f2] px-4 py-1.5',
                            'text-2.5 leading-2.5 font-medium tracking-[0.05rem] text-[#141414] uppercase',
                            'md:px-3.5 md:py-1.5 md:text-2.5',
                        )}
                    >
                        Featured
                    </span>
                </div>

                {/* Center: Author Name and Title */}
                <div className="flex flex-1 flex-col items-center justify-center px-5 text-center md:-mt-10 md:px-20">
                    {/* Author/Professional Name (or Subtitle override) */}
                    {displayName && (
                        <p
                            className={cn(
                                'mb-4 text-[0.875rem] font-medium text-white/90 uppercase',
                                'leading-normal tracking-[0.112rem]',
                                'md:mb-6 md:text-[1.4rem] md:tracking-[0.112rem]',
                            )}
                        >
                            {displayName}
                        </p>
                    )}

                    {/* Main Title */}
                    <h2
                        className={cn(
                            'mx-auto font-twk-ghost text-[2rem] leading-tight font-normal text-white',
                            'tracking-[-0.05rem]',
                            'md:max-w-250 md:text-[4.2rem] md:leading-[1.1] md:tracking-[-0.084rem]',
                        )}
                    >
                        {banner.itemTitle}
                    </h2>
                </div>
            </div>
        </>
    );

    // Wrapper classes for the banner container
    const wrapperClasses = cn(
        'relative h-78.75 w-full overflow-hidden', // Mobile: ~315px
        'block cursor-pointer',
        'focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:outline-hidden',
        'md:h-[39.375rem]', // Desktop: 630px
    );

    // Render as external link or internal Remix Link
    if (isExternalLink) {
        return (
            <section ref={sectionRef} aria-label="Featured content">
                <a
                    href={banner.ctaLink}
                    onClick={handleBannerClick}
                    className={wrapperClasses}
                    aria-label={`Read more about ${banner.itemTitle}`}
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    {bannerContent}
                </a>
            </section>
        );
    }

    return (
        <section ref={sectionRef} aria-label="Featured content">
            <Link
                to={banner.ctaLink}
                onClick={handleBannerClick}
                className={wrapperClasses}
                aria-label={`Read more about ${banner.itemTitle}`}
                prefetch="intent"
            >
                {bannerContent}
            </Link>
        </section>
    );
};
